import {ref, reactive, computed, unref, watch} from 'vue';
import {Notification} from '@baidu/acud-element-ui';
import {watchImmediate, tryOnBeforeMount, tryOnUnmounted} from '@vueuse/core';
import {defineStore} from 'pinia';
import {map, get} from 'lodash-es';
import pinia from '@/znzs/new_src/store/index.js';
import {addClass, removeClass, concurrencyQuerySession} from '@/znzs/new_src/utils/index';
import {trackBehavior, useStore} from '@/znzs/new_src/hooks/index';
import {getSessionMessages, closeCurrentSession} from '@/znzs/new_src/api/index';
import {useCommonStore} from '@/znzs/new_src/store/commonStore.js';
import {useImStore} from '@/znzs/new_src/store/imStore';
import {DIALOG_TYPE} from '@/znzs/new_src/constant/index';
import {filterMessage} from '@/znzs/src/utils/index';
import {onSendQuestion, onStreamStop, onStreamComplete} from '@/znzs/new_src/utils/events.js';

const queryMap = {};
function queryHistory(id, cache) {
    let task = queryMap[id];
    if (!task) {
        task = getSessionMessages(id);
    }
    if (cache) {
        queryMap[id] = task;
    }
    return task;
}

export const useChatStore = defineStore('ChatStore', () => {
    const commonStore = useCommonStore();
    const imStore = useImStore();

    const vuexStore = useStore();
    // 与大模型对话记录
    const messages = computed(() => vuexStore.getters.chattingRecordsLists);
    // 当前激活的会话信息
    const sessionInfo = ref();
    // 当前激活的会话的会话ID
    const activeSessionId = computed(() => unref(sessionInfo)?.znzsSessionId || '');
    // 当前展示在界面上的会话的会话ID
    const currentSessionId = ref('');
    // 当前加载的是否是历史记录
    const currentSessionIdIsHistory = computed(() => unref(currentSessionId) !== unref(activeSessionId));
    // 是否允许新建会话
    const allowCreate = ref(true);

    const resource = reactive({
        visible: false,
        list: [],
        link: '',
        mode: 'list',
    });

    watchImmediate(() => resource.visible, visible => {
        if (visible) {
            window.top.postMessage({
                channel: 'sideBarCollapsed',
                data: true,
            });
            addClass(document.body, 'resource-visible');
            trackBehavior({
                name: '浮窗触发次数',
            });
        }
        else {
            removeClass(document.body, 'resource-visible');
        }
    });

    function setChatMessage(data) {
        vuexStore.commit('adjustChatRecordsList', data || []);
    }

    /**
     * 根据会话ID获取消息记录
     *
     * @param {string} sessionId - 会话ID
     * @returns {Promise<void>} 无返回值
     */
    async function getMessagesBySessionId(sessionId) {
        if (!sessionId) {
            return;
        }
        // 关闭文档、知识浮窗
        resource.visible = false;
        setCurrentSessionId(sessionId);
        commonStore.pageLoading = true;
        try {
            const res = await queryHistory(sessionId, sessionId !== unref(activeSessionId));
            if (res.success) {
                setChatMessage(map(
                    filterMessage(get(res, 'data', [])),
                    item => {
                        item.fromCache = true;
                        // 如果content不是一个链接，就认为是一个自定义自助工具，用selfHelpToolName去重置display
                        if (
                            item.dialogType === DIALOG_TYPE.SELF_HELP_TOOL
                            && !/^https?:\/\//.test(item.dialogInfo.content)
                        ) {
                            item.dialogInfo.display = item.selfHelpToolName;
                        }
                        return item;
                    }));
            }
        }
        catch {}
        finally {
            commonStore.pageLoading = false;
        }
    }

    async function clearMessage() {
        if (!unref(messages)?.length) {
            return;
        }
        setChatMessage([]);
    }

    function setSessionInfo(info) {
        sessionInfo.value = info;
    }
    function setCurrentSessionId(id) {
        currentSessionId.value = id;
    }

    /**
     * 创建新会话
     */
    async function createNewSession() {
        if (!allowCreate.value) {
            Notification.warning('请等待回复完成之后再新建对话');
            return;
        }
        // 清空聊天记录
        clearMessage();
        // 重置当前会话ID
        setCurrentSessionId('');
        // 关闭文档、知识浮窗
        resource.visible = false;
        // 提前清空已激活会话信息
        setSessionInfo({});
        try {
            const sessionRes = await concurrencyQuerySession();
            if (sessionRes.success) {
                if (sessionRes?.data.znzsSessionId) {
                    await closeCurrentSession();
                }
            }
        }
        catch {}
    }

    function setResource(list) {
        if (list?.length) {
            resource.list = list;
            resource.visible = true;
        }
    }

    function showLink(link) {
        if (link) {
            resource.link = link;
            resource.mode = 'link';
            resource.visible = true;
        }
    }

    function backToList() {
        resource.mode = 'list';
        resource.link = '';
    }

    let timer = null;
    async function loopSyncSession() {
        try {
            if (!imStore.socketConnected) {
                await concurrencyQuerySession();
            }
        }
        catch {}
        finally {
            timer = setTimeout(loopSyncSession, 5000);
        }
    }

    watch(activeSessionId, id => {
        // 用户输入第一条消息创建会话，要自动同步为当前会话
        if (id && !currentSessionId.value) {
            currentSessionId.value = id;
        }
    }, {immediate: true});

    // 开发发送问题的时候禁止新建会话
    onSendQuestion(() => {
        allowCreate.value = false;
    });

    // 流式生成停止时允许新建会话
    onStreamStop(() => {
        allowCreate.value = true;
    });

    // 流式生成完成时允许新建会话
    onStreamComplete(() => {
        allowCreate.value = true;
    });

    tryOnBeforeMount(() => {
        loopSyncSession();
    });

    tryOnUnmounted(() => {
        if (timer) {
            clearTimeout(timer);
        }
    });

    return {
        resource,
        messages,
        allowCreate,
        sessionInfo,
        activeSessionId,
        currentSessionId,
        currentSessionIdIsHistory,
        showLink,
        backToList,
        setResource,
        clearMessage,
        setSessionInfo,
        createNewSession,
        setCurrentSessionId,
        getMessagesBySessionId,
    };
});

export const useChatStoreOut = () => useChatStore(pinia);